import type { MessageKey } from '@dijitalapp/i18n';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat, useChatStore } from '../lib/chat-context';
import { formatCode, type PeerTrust, type VerificationView } from '../lib/e2ee';
import { Dialog } from './GroupDialogs';
import { QrGrid, QrVerifyScanner } from './QrVerify';
import { Icon } from './Icon';

/** The E2EE engine once ready, plus a version that changes whenever its state does. */
function useE2ee() {
  const store = useChatStore();
  const chat = useChat();
  return { e2ee: chat.e2eeReady ? (store?.getE2ee() ?? null) : null, version: chat.e2eeVersion };
}

/** Trust of a person, refreshed whenever the encryption state changes. */
function usePeerTrust(userId: string | undefined): PeerTrust | null {
  const { e2ee, version } = useE2ee();
  const [state, setState] = useState<{ key: string; trust: PeerTrust } | null>(null);
  const key = `${userId}:${version}`;
  useEffect(() => {
    if (!e2ee || !userId) return;
    let alive = true;
    void e2ee.trustOf(userId).then((trust) => alive && setState({ key, trust }));
    return () => {
      alive = false;
    };
  }, [e2ee, userId, key]);
  return state?.key === key ? state.trust : null;
}

/** Conversation header badge: verified / not verified / identity changed; opens the verify dialog. */
export function TrustBadge({ userId, name }: { userId: string; name: string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const trust = usePeerTrust(userId);
  const [open, setOpen] = useState(false);
  if (!trust) return null;
  const label = trust === 'verified' ? `🔒✓ ${t('e2ee.trust.verified')}` : trust === 'changed' ? `⚠ ${t('e2ee.trust.changed')}` : `🔒 ${t('e2ee.trust.unverified')}`;
  return (
    <>
      <button type="button" className={`btn trust-badge trust-${trust}`} onClick={() => setOpen(true)} title={t('e2ee.label')}>
        {label}
      </button>
      {open && <VerifyDialog userId={userId} name={name} trust={trust} onClose={() => setOpen(false)} />}
    </>
  );
}

function VerifyDialog({ userId, name, trust, onClose }: { userId: string; name: string; trust: PeerTrust; onClose: () => void }) {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const { e2ee } = useE2ee();
  const [codes, setCodes] = useState<{ theirs: string | null; mine: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!e2ee || !me) return;
    let alive = true;
    void Promise.all([e2ee.safetyCode(userId), e2ee.safetyCode(me.id)]).then(([theirs, mine]) => alive && setCodes({ theirs, mine }));
    return () => {
      alive = false;
    };
  }, [e2ee, me, userId]);

  const run = (fn: () => Promise<void>) => {
    setError(null);
    fn().then(onClose, (err: unknown) => setError(err instanceof Error && !('code' in err) ? err.message : t(errorMessageKey(err))));
  };

  return (
    <Dialog title={trust === 'changed' ? t('e2ee.changedTitle', { name }) : t('e2ee.verifyTitle', { name })} onClose={onClose}>
      {trust === 'changed' ? <p className="alert alert-error">{t('e2ee.changedBody')}</p> : <p>{t('e2ee.verifyIntro', { name })}</p>}
      {codes && !codes.theirs ? (
        <p className="muted">{t('e2ee.noIdentity')}</p>
      ) : (
        <>
          <section className="stack" style={{ gap: 'var(--space-2)' }}>
            <h3>{t('e2ee.verifyEmoji')}</h3>
            <p className="hint">{t('e2ee.verifyEmojiHint')}</p>
            <div className="row">
              <button className="btn btn-primary" type="button" disabled={!e2ee} onClick={() => run(() => e2ee!.verifyUser(userId))}>
                {trust === 'changed' ? t('e2ee.reverify') : t('e2ee.verifyEmoji')}
              </button>
            </div>
          </section>
          <section className="stack" style={{ gap: 'var(--space-2)' }}>
            <h3>{t('e2ee.safetyCode')}</h3>
            <div className="safety-codes">
              <div>
                <span className="hint">{t('e2ee.theirCode', { name })}</span>
                <code>{codes?.theirs ?? '…'}</code>
              </div>
              <div>
                <span className="hint">{t('e2ee.yourCode')}</span>
                <code>{codes?.mine ?? '…'}</code>
              </div>
            </div>
            <p className="hint">{t('e2ee.codeHint', { name })}</p>
            <div className="row">
              <button className="btn" type="button" disabled={!codes?.theirs} onClick={() => run(() => e2ee!.verifyByCode(userId))}>
                {t('e2ee.codesMatch')}
              </button>
            </div>
          </section>
        </>
      )}
      {error && <p className="alert alert-error">{error}</p>}
      <div className="row">
        {trust === 'changed' && (
          <button className="btn btn-danger" type="button" onClick={() => run(() => e2ee!.acceptIdentityChange(userId))}>
            {t('e2ee.acceptChange')}
          </button>
        )}
        <span className="spacer" />
        <button className="btn" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Dialog>
  );
}

/** Shows the verification in progress (incoming request, emoji, result); mounted once in the shell. */
export function VerificationCenter({ nameOf }: { nameOf: (userId: string) => string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { e2ee, version } = useE2ee();
  void version; // re-render on every state change
  const [scanning, setScanning] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const flows: VerificationView[] = e2ee?.verifications() ?? [];
  // While several devices were asked, show the one that progressed furthest.
  const order: Record<VerificationView['phase'], number> = { emoji: 5, 'qr-show': 5, 'qr-scanned': 6, 'qr-waiting': 5, confirmed: 4, done: 7, ready: 3, requested: 2, cancelled: 1 };
  const flow = [...flows].sort((a, b) => order[b.phase] - order[a.phase])[0];
  if (!e2ee || !flow) return null;
  const close = () => {
    setScanning(false);
    setProblem(null);
    flows.forEach((f) => e2ee.dismissVerification(f.flowId));
  };
  const run = (fn: () => Promise<void>, failKey: MessageKey = 'e2ee.qr.failed') => {
    setProblem(null);
    void fn().catch(() => setProblem(t(failKey)));
  };
  const name = flow.self ? t('e2ee.thisDevice') : nameOf(flow.userId);
  return (
    <Dialog title={flow.self ? t('e2ee.verifyThisDevice') : t('e2ee.verifyTitle', { name })} onClose={close}>
      {flow.phase === 'requested' && flow.incoming && (
        <>
          <p>{flow.self ? t('e2ee.requestIncomingSelf') : t('e2ee.requestIncoming', { name })}</p>
          <div className="row">
            <button className="btn" type="button" onClick={() => void e2ee.cancelVerification(flow.flowId).then(close)}>
              {t('e2ee.decline')}
            </button>
            <span className="spacer" />
            <button className="btn btn-primary" type="button" onClick={() => void e2ee.acceptVerification(flow.flowId)}>
              {t('e2ee.accept')}
            </button>
          </div>
        </>
      )}
      {flow.phase === 'ready' && !scanning && (
        <>
          <p>{t('e2ee.qr.choose')}</p>
          <div className="verify-methods">
            <button className="btn btn-primary" type="button" onClick={() => run(() => e2ee.showQr(flow.flowId))}>
              {t('e2ee.qr.show')}
            </button>
            <button className="btn btn-primary" type="button" onClick={() => setScanning(true)}>
              {t('e2ee.qr.scan')}
            </button>
            <button className="btn" type="button" onClick={() => run(() => e2ee.startEmoji(flow.flowId))}>
              {t('e2ee.qr.emoji')}
            </button>
          </div>
          {problem && (
            <p className="alert alert-error" role="alert">
              {problem}
            </p>
          )}
          <div className="row">
            <span className="spacer" />
            <button className="btn" type="button" onClick={() => void Promise.all(flows.map((f) => e2ee.cancelVerification(f.flowId))).then(close)}>
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}
      {flow.phase === 'ready' && scanning && (
        <QrVerifyScanner
          onCancel={() => setScanning(false)}
          onCode={(bytes) => {
            setScanning(false);
            run(() => e2ee.scanQr(flow.flowId, bytes), 'e2ee.qr.badCode');
          }}
        />
      )}
      {flow.phase === 'qr-show' && flow.qr && (
        <>
          <p>{t('e2ee.qr.showHint')}</p>
          <div className="qr-verify-wrap">
            <QrGrid size={flow.qr.size} modules={flow.qr.modules} label={t('e2ee.qr.codeLabel')} />
          </div>
          <div className="row">
            <button className="btn" type="button" onClick={() => run(() => e2ee.startEmoji(flow.flowId))}>
              {t('e2ee.qr.emojiInstead')}
            </button>
            <span className="spacer" />
            <button className="btn" type="button" onClick={() => void e2ee.cancelVerification(flow.flowId)}>
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}
      {flow.phase === 'qr-scanned' && (
        <>
          <p>{t('e2ee.qr.scannedQuestion')}</p>
          <div className="row">
            <button className="btn btn-danger" type="button" onClick={() => void e2ee.cancelVerification(flow.flowId)}>
              {t('e2ee.qr.scannedNo')}
            </button>
            <span className="spacer" />
            <button className="btn btn-primary" type="button" onClick={() => run(() => e2ee.confirmQrScanned(flow.flowId))}>
              {t('e2ee.qr.scannedYes')}
            </button>
          </div>
        </>
      )}
      {flow.phase === 'qr-waiting' && (
        <p className="muted" role="status">
          ✓ {t('e2ee.qr.waiting')}
        </p>
      )}
      {flow.phase === 'requested' && !flow.incoming && (
        <>
          <p className="muted" role="status">
            {t('e2ee.waitingAccept')}
          </p>
          <div className="row">
            <span className="spacer" />
            <button className="btn" type="button" onClick={() => void Promise.all(flows.map((f) => e2ee.cancelVerification(f.flowId))).then(close)}>
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}
      {flow.phase === 'emoji' && (
        <>
          <p>{t('e2ee.emojiHint')}</p>
          <ol className="emoji-grid" aria-label={t('e2ee.emojiHint')}>
            {flow.emoji.map((e, i) => (
              <li key={i}>
                <span className="emoji-symbol" aria-hidden>
                  {e.symbol}
                </span>
                <span className="hint">{e.description}</span>
              </li>
            ))}
          </ol>
          <div className="row">
            <button className="btn btn-danger" type="button" onClick={() => void e2ee.cancelVerification(flow.flowId)}>
              {t('e2ee.noMatch')}
            </button>
            <span className="spacer" />
            <button className="btn btn-primary" type="button" onClick={() => void e2ee.confirmEmoji(flow.flowId)}>
              {t('e2ee.match')}
            </button>
          </div>
        </>
      )}
      {flow.phase === 'confirmed' && (
        <p className="muted" role="status">
          {t('e2ee.waitingOther')}
        </p>
      )}
      {(flow.phase === 'done' || flow.phase === 'cancelled') && (
        <>
          <p role="status" className={flow.phase === 'done' ? '' : 'alert alert-error'}>
            {flow.phase === 'done' ? t('e2ee.done') : t('e2ee.cancelled')}
          </p>
          <div className="row">
            <span className="spacer" />
            <button className="btn btn-primary" type="button" onClick={close}>
              {t('common.close')}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/** One-time display of a newly created recovery key. */
export function RecoveryKeyPrompt() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { e2ee } = useE2ee();
  const [copied, setCopied] = useState(false);
  const key = e2ee?.recoveryKeyToShow;
  if (!e2ee || !key) return null;
  const done = () => {
    setCopied(false);
    e2ee.acknowledgeRecoveryKey();
  };
  return (
    <Dialog title={t('e2ee.recoveryTitle')} onClose={done}>
      <p>{t('e2ee.recoveryBody')}</p>
      <code className="recovery-key">{formatCode(key)}</code>
      <div className="row">
        <button
          className="btn"
          type="button"
          onClick={() => void navigator.clipboard.writeText(key).then(() => setCopied(true), () => undefined)}
        >
          {copied ? t('group.linkCopied') : t('group.copyLink')}
        </button>
        <span className="spacer" />
        <button className="btn btn-primary" type="button" onClick={done}>
          {t('e2ee.saved')}
        </button>
      </div>
    </Dialog>
  );
}

function RecoveryRestoreForm() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { e2ee } = useE2ee();
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!e2ee || !value.trim()) return;
    setStatus(null);
    e2ee.restoreFromRecoveryKey(value).then(
      (count) => {
        setValue('');
        setStatus({ ok: true, text: t('e2ee.restored', { count }) });
      },
      () => setStatus({ ok: false, text: t('e2ee.wrongRecovery') }),
    );
  };
  return (
    <form className="row" onSubmit={submit} style={{ flexWrap: 'nowrap' }}>
      <label className="visually-hidden" htmlFor="recovery-key">
        {t('e2ee.enterRecovery')}
      </label>
      <input
        id="recovery-key"
        className="input"
        autoComplete="off"
        spellCheck={false}
        placeholder={t('e2ee.enterRecovery')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button className="btn" disabled={!value.trim()}>
        {t('e2ee.restore')}
      </button>
      {status && (
        <span role="status" className={status.ok ? 'hint' : 'hint e2ee-warning'}>
          {status.text}
        </span>
      )}
    </form>
  );
}

/** Banner while this device is not verified by the person's identity. */
/**
 * Whether another device was used recently enough to approve this one. Old sessions that were never signed
 * out (a closed tab, an old browser) do not count: nobody is there to accept the request.
 */
function useOtherSessions(): boolean | null {
  const [other, setOther] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ devices: { current: boolean; lastActiveAt: string }[] }>('GET', '/devices').then(
      (r) => alive && setOther(r.devices.some((d) => !d.current && Date.now() - Date.parse(d.lastActiveAt) < 15 * 60_000)),
      () => alive && setOther(false),
    );
    return () => {
      alive = false;
    };
  }, []);
  return other;
}

/**
 * Making this device trusted, in the simplest order for the person's situation:
 * another signed-in device → approve from there; none → set encryption up again here (password only);
 * a saved recovery key is always available as a secondary option.
 */
export function DeviceTrustFixer() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { e2ee } = useE2ee();
  const other = useOtherSessions();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showReset, setShowReset] = useState(false);
  if (!e2ee || other === null) return <p className="muted">{t('app.loading')}</p>;
  const resetOpen = !other || showReset;

  const reset = (e: FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    setError(null);
    e2ee.resetIdentity(password).then(
      () => setPassword(''),
      (err: unknown) => setError(t(errorMessageKey(err))),
    ).finally(() => setBusy(false));
  };

  return (
    <div className="fixer stack">
      {other && (
        <div className="fixer-step">
          <strong>{t('e2ee.fix.otherTitle')}</strong>
          <span className="hint">{t('e2ee.fix.otherBody')}</span>
          <div className="row">
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={waiting}
              onClick={() => {
                setWaiting(true);
                void e2ee.verifyThisDevice().catch((err: unknown) => (setWaiting(false), setError(String(err))));
              }}
            >
              {waiting ? t('e2ee.fix.waiting') : t('e2ee.verifyThisDevice')}
            </button>
            {!showReset && (
              <button className="link-button" type="button" onClick={() => setShowReset(true)}>
                {t('e2ee.fix.noAccess')}
              </button>
            )}
          </div>
        </div>
      )}
      {resetOpen && (
        <form className="fixer-step" onSubmit={reset}>
          <strong>{t('e2ee.fix.resetTitle')}</strong>
          <span className="hint">{other ? t('e2ee.fix.resetBodyOther') : t('e2ee.fix.resetBody')}</span>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <label className="visually-hidden" htmlFor="fixer-password">
              {t('auth.password')}
            </label>
            <input
              id="fixer-password"
              className="input"
              type="password"
              autoComplete="current-password"
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button className="btn btn-primary" disabled={!password || busy}>
              {busy ? t('app.loading') : t('e2ee.fix.resetAction')}
            </button>
          </div>
        </form>
      )}
      {error && <p className="alert alert-error">{error}</p>}
      {showRecovery ? (
        <div className="fixer-step">
          <strong>{t('e2ee.fix.recoveryTitle')}</strong>
          <RecoveryRestoreForm />
        </div>
      ) : (
        <button className="link-button" type="button" onClick={() => setShowRecovery(true)}>
          {t('e2ee.fix.haveRecovery')}
        </button>
      )}
    </div>
  );
}

/**
 * Shown only when it matters: this device is not verified AND the person has end-to-end encrypted chats.
 * Without encrypted chats there is nothing to read or write here, so there is nothing to fix.
 */
export function DeviceTrustBanner() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { e2ee } = useE2ee();
  const chat = useChat();
  const [expanded, setExpanded] = useState(false);
  // "Later" hides it for this browser session.
  const [hidden, setHidden] = useState(() => {
    try {
      return sessionStorage.getItem('e2ee-banner-hidden') === '1';
    } catch {
      return false;
    }
  });
  const hasEncryptedChats = chat.conversations.some((c) => c.e2ee);
  if (!e2ee || e2ee.deviceVerified || hidden || !hasEncryptedChats) return null;
  const hide = () => {
    setHidden(true);
    try {
      sessionStorage.setItem('e2ee-banner-hidden', '1');
    } catch {
      /* private mode: hidden until reload */
    }
  };
  return (
    <div className="device-trust-banner" role="region" aria-label={t('e2ee.deviceUnverifiedTitle')}>
      <div className="device-trust-head">
        <Icon name="key" size={18} />
        <span className="device-trust-text">
          <strong>{t('e2ee.deviceUnverifiedTitle')}</strong>
          <span className="muted"> — {t('e2ee.deviceUnverifiedShort')}</span>
        </span>
        <button className="btn btn-sm" type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
          {t('e2ee.fix')}
        </button>
        <button className="icon-btn" type="button" onClick={hide} title={t('e2ee.later')}>
          <Icon name="close" size={16} label={t('e2ee.later')} />
        </button>
      </div>
      {expanded && <DeviceTrustFixer />}
    </div>
  );
}

/** Settings → end-to-end encryption. */
export function E2eeSettings() {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const { e2ee, version } = useE2ee();
  const [code, setCode] = useState<{ key: string; value: string | null } | null>(null);
  const [recovery, setRecovery] = useState<string | null | undefined>(undefined);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const key = `${me?.id}:${version}`;
  useEffect(() => {
    if (!e2ee || !me) return;
    let alive = true;
    void e2ee.safetyCode(me.id).then((value) => alive && setCode({ key, value }));
    return () => {
      alive = false;
    };
  }, [e2ee, me, key]);

  if (!e2ee) {
    return (
      <section className="card stack" aria-labelledby="e2ee-title">
        <h2 id="e2ee-title" className="card-title">{t('e2ee.settingsTitle')}</h2>
        <p className="muted">{t('e2ee.loading')}</p>
      </section>
    );
  }
  const reset = (e: FormEvent) => {
    e.preventDefault();
    if (!window.confirm(t('e2ee.resetBody'))) return;
    setError(null);
    e2ee.resetIdentity(password).then(
      () => setPassword(''),
      (err: unknown) => setError(t(errorMessageKey(err))),
    );
  };
  return (
    <section className="card stack" aria-labelledby="e2ee-title">
      <h2 id="e2ee-title" className="card-title">{t('e2ee.settingsTitle')}</h2>
      <div className="toggle-row">
        <span>{t('e2ee.thisDevice')}</span>
        <span className="spacer" />
        <strong>{e2ee.deviceVerified ? `✓ ${t('e2ee.trust.verified')}` : t('e2ee.trust.unverified')}</strong>
      </div>
      <div className="stack" style={{ gap: 'var(--space-1)' }}>
        <span className="hint">{t('e2ee.yourCode')}</span>
        <code className="recovery-key">{code?.key === key ? (code.value ?? '—') : '…'}</code>
      </div>
      {!e2ee.deviceVerified && <DeviceTrustFixer />}
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <div className="row">
          <button className="btn" type="button" onClick={() => void e2ee.recoveryKey().then(setRecovery)}>
            {t('e2ee.showRecovery')}
          </button>
        </div>
        {recovery === null && <p className="hint">{t('e2ee.notOnThisDevice')}</p>}
        {recovery && <code className="recovery-key">{formatCode(recovery)}</code>}
        <RecoveryRestoreForm />
      </div>
      <details>
        <summary>{t('e2ee.resetTitle')}</summary>
        <form className="stack" onSubmit={reset} style={{ marginTop: 'var(--space-3)' }}>
          <p className="hint">{t('e2ee.resetBody')}</p>
          <div className="field">
            <label htmlFor="e2ee-reset-password">{t('auth.password')}</label>
            <input
              id="e2ee-reset-password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="row">
            <button className="btn btn-danger" disabled={!password}>
              {t('e2ee.reset')}
            </button>
          </div>
          {error && <p className="alert alert-error">{error}</p>}
        </form>
      </details>
    </section>
  );
}
